/**
 * Restore Snapshot Modal Component
 */

import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { vmApi } from '../../services/vmApi';
import { VMSnapshot, VMSnapshotRestore } from '../../types/vm';

interface RestoreSnapshotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  snapshot: VMSnapshot;
}

const RestoreSnapshotModal: React.FC<RestoreSnapshotModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  snapshot,
}) => {
  const queryClient = useQueryClient();
  const [restoreMode, setRestoreMode] = useState<'new_vm' | 'replace'>('new_vm');
  const [newVMName, setNewVMName] = useState('');

  // Restore snapshot mutation
  const restoreMutation = useMutation({
    mutationFn: (data: VMSnapshotRestore) => vmApi.restoreSnapshot(snapshot.id, data),
    onSuccess: () => {
      toast.success('Snapshot restore initiated');
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to restore snapshot');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (restoreMode === 'new_vm' && !newVMName.trim()) {
      toast.error('Please enter a name for the new VM');
      return;
    }

    if (restoreMode === 'replace') {
      if (!window.confirm(
        'Are you sure you want to restore this snapshot? This will replace the current VM state and cannot be undone.'
      )) {
        return;
      }
    }

    restoreMutation.mutate({
      mode: restoreMode,
      new_vm_name: restoreMode === 'new_vm' ? newVMName.trim() : undefined,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-black bg-opacity-50" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-gray-900 border border-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-gray-900 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Restore Snapshot</h3>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-300"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-md">
              <p className="text-sm text-blue-400">
                <strong>Snapshot:</strong> {snapshot.name}
              </p>
              {snapshot.description && (
                <p className="text-sm text-blue-400/80 mt-1">{snapshot.description}</p>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Restore Mode *
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="new_vm"
                      checked={restoreMode === 'new_vm'}
                      onChange={(e) => setRestoreMode(e.target.value as 'new_vm' | 'replace')}
                      className="mr-2 text-primary-500 focus:ring-primary-500 bg-gray-800 border-gray-600"
                    />
                    <span className="text-sm text-gray-300">
                      Create New VM
                      <span className="text-gray-400 ml-1">(Creates a new VM from this snapshot)</span>
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="replace"
                      checked={restoreMode === 'replace'}
                      onChange={(e) => setRestoreMode(e.target.value as 'new_vm' | 'replace')}
                      className="mr-2 text-primary-500 focus:ring-primary-500 bg-gray-800 border-gray-600"
                    />
                    <span className="text-sm text-gray-300">
                      Replace Existing VM
                      <span className="text-gray-400 ml-1">(Restores snapshot to the original VM)</span>
                    </span>
                  </label>
                </div>
              </div>

              {restoreMode === 'new_vm' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    New VM Name *
                  </label>
                  <input
                    type="text"
                    value={newVMName}
                    onChange={(e) => setNewVMName(e.target.value)}
                    placeholder="e.g., restored-vm-backup"
                    className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                    maxLength={253}
                  />
                </div>
              )}

              {restoreMode === 'replace' && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-md">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-300">Warning</h3>
                      <div className="mt-2 text-sm text-yellow-400/80">
                        <p>
                          Restoring this snapshot will replace the current VM state. All current data will be lost and cannot be recovered.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="submit"
                  disabled={restoreMutation.isPending}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-500 text-base font-medium text-white hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:col-start-2 sm:text-sm disabled:opacity-50"
                >
                  {restoreMutation.isPending ? 'Restoring...' : 'Restore Snapshot'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-600 shadow-sm px-4 py-2 bg-gray-800 text-base font-medium text-gray-300 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestoreSnapshotModal;

